import { useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Textarea } from '@nextui-org/react';
import { extractJsonObject } from '../lib/json';

// El asistente de traducción de los textos del Inicio de la app (carruseles y
// popup «Incluido en Premium»). Como el de Popups: se copia el prompt, se le
// pasa a ChatGPT y se pega aquí el JSON que devuelve.
//
//   pending     textos a los que les falta algún idioma: [{ id, es, … }]
//   everything  todos los que tienen español (con la casilla «incluir también
//               los ya traducidos»)
//   promptFor   (textos) → el prompt
//   onApply     (textos, json) → { applied }: cuántos se han puesto. Con 0 se
//               avisa y no se cierra; cerrar es cosa de quien lo abre.
//   targets     a cuántos idiomas se traduce
//   example     una clave de muestra para el texto de ayuda
export default function TranslateDialog({ pending, everything, promptFor, onApply, onClose, targets, example = 'w1_title' }) {
  const [all, setAll] = useState(pending.length === 0);
  const fields = all ? everything : pending;
  const prompt = useMemo(() => promptFor(fields), [promptFor, fields]);
  const [json, setJson] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setError(`No se pudo copiar: ${e.message || e}. Selecciona el texto y cópialo a mano.`);
    }
  }

  function apply() {
    let parsed;
    try { parsed = extractJsonObject(json); } catch (e) { setError(e.message); return; }
    const result = onApply(fields, parsed);
    if (!result.applied) setError(`Ese JSON no trae traducciones para estos textos. Tiene que llevar las mismas claves que el prompt (${example}…).`);
  }

  return (
    <Modal isOpen onOpenChange={(isOpen) => { if (!isOpen) onClose(); }} size="5xl" scrollBehavior="inside" placement="center">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <span>Traducir con ChatGPT</span>
          <span className="text-xs font-normal text-default-500">
            {fields.length} {fields.length === 1 ? 'texto' : 'textos'} en español → {targets} idiomas. El inglés que ya hayas escrito a mano no se toca.
          </span>
        </ModalHeader>
        <ModalBody className="flex flex-col gap-4">
          <div className="rounded-xl border border-default-200 p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">1 · Prompt</h3>
                <p className="text-xs text-default-500">Cópialo, pégalo en ChatGPT y pide la respuesta en JSON.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {pending.length && everything.length > pending.length ? (
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-default-600">
                    <input type="checkbox" checked={all} onChange={(e) => { setAll(e.target.checked); setError(''); }} />
                    Incluir también los ya traducidos ({everything.length - pending.length})
                  </label>
                ) : null}
                <Button size="sm" variant="flat" color={copied ? 'success' : 'primary'} onPress={copy} data-copy-prompt>{copied ? 'Copiado' : 'Copiar prompt'}</Button>
              </div>
            </div>
            <Textarea aria-label="Prompt" value={prompt} minRows={8} maxRows={14} readOnly classNames={{ input: 'font-mono text-xs' }} />
          </div>
          <div className="rounded-xl border border-default-200 p-4">
            <h3 className="mb-3 text-sm font-semibold">2 · Respuesta JSON de ChatGPT</h3>
            <Textarea
              aria-label="Respuesta JSON" placeholder={`Pega aquí el JSON, por ejemplo {"translations":{"${example}":{"en":"…","fr":"…"}}}`}
              value={json} onValueChange={(value) => { setJson(value); setError(''); }} minRows={8} maxRows={14} classNames={{ input: 'font-mono text-xs' }}
            />
            {error ? <div role="alert" className="mt-3 rounded-md border border-danger-200 bg-danger-50 p-3 text-xs text-danger-700">{error}</div> : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cerrar</Button>
          <Button color="primary" isDisabled={!json.trim() || !fields.length} onPress={apply}>Aplicar traducciones</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
